// apps/ers/src/app/modules/ers-leave-management/components/avail-gift-of-time-page/avail-gift-of-time-page.component.ts
import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiNotificationService } from '@rcs/notify';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';

import { LeaveHolidayPanelComponent } from '../leave-holiday-panel/leave-holiday-panel.component';
import { SuccessDialogComponent } from '../../dialogs/success-dialog/success-dialog';
import { Manager, GiftedLeaveBalance } from '../../models/leave.model';
import { LeaveService } from '../../services/leave.service';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { formatDateMDY, getDayName, toISODate } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSearchableSelectComponent } from '@rcs/ers-elementals';

@Component({
  selector: 'ers-avail-gift-of-time-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    LeaveHolidayPanelComponent,
    LmLoadingOverlayComponent,
    ErsSearchableSelectComponent
  ],
  templateUrl: './avail-gift-of-time-page.component.html',
  styleUrls: ['./avail-gift-of-time-page.component.scss'],
})
export class AvailGiftOfTimePageComponent implements OnInit {
  // form
  availForm: FormGroup;

  // chip selection
  selectedManager: Manager | null = null;

  // data
  managers: Manager[] = [];
  giftedBalance: GiftedLeaveBalance = { totalGifted: 0, totalUsed: 0, balanceRemaining: 0, expiresOn: null };
  expiresInDays: number | null = null;

  // right panel data
  companyHolidayRows: any[] = [];
  optionalHolidayRows: any[] = [];
  pendingWorkedRows: any[] = [];

  holidayColDefs: ColDef[] = [];
  workedDateColDefs: ColDef[] = [];

  isLoading = false;
  isSubmitting = false;

  private destroyRef = inject(DestroyRef);
  private notify = inject(ApiNotificationService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private leaveService: LeaveService,
    private dialog: MatDialog,
  ) {
    this.availForm = this.fb.group({
      requestToId:  [''],
      startDate:    [null, Validators.required],
      endDate:      [null, Validators.required],
      description:  [''],
    });
  }

  ngOnInit(): void {
    this.setupColDefs();
    this.loadData();
  }

  private setupColDefs(): void {
    this.holidayColDefs = [
      {
        headerName: 'Holiday Name',
        field: 'holidayName',
        flex: 1,
        minWidth: 160,
        cellRenderer: (p: ICellRendererParams) => {
          const star = p.data?.isMandatory
            ? `<span style="color:#FFC107;margin-right:5px;font-size:14px;">★</span>`
            : `<span style="display:inline-block;width:19px;"></span>`;
          return `<span style="display:flex;align-items:center;">${star}${p.value ?? ''}</span>`;
        },
      },
      { headerName: 'Date', field: 'date', width: 110 },
      { headerName: 'Day',  field: 'day',  width: 110 },
      { headerName: 'Type', field: 'type', width: 100 },
    ];
    this.workedDateColDefs = [
      {
        headerName: 'Date of Worked',  field: 'dateWorked',    flex: 1,
        sort: 'desc', sortIndex: 0,
        comparator: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      },
      { headerName: 'Requested Date',  field: 'requestedDate', flex: 1 },
      { headerName: 'Status',          field: 'status',        width: 100 },
    ];
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      managers:      this.leaveService.getManagers(),
      holidays:      this.leaveService.getHolidays(),
      workedDates:   this.leaveService.getWorkedDates(),
      giftedBalance: this.leaveService.getMyGiftedLeaveBalance().pipe(
        catchError(() => of({ totalGifted: 0, totalUsed: 0, balanceRemaining: 0, expiresOn: null }))
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ managers, holidays, workedDates, giftedBalance }) => {
        this.isLoading = false;
        this.managers      = managers;
        this.giftedBalance = giftedBalance;
        this.expiresInDays = this.computeExpiresInDays(giftedBalance.expiresOn);

        const rows = holidays.map(h => ({
          holidayName: h.holidayName,
          date:        this.fmt(h.holidayDate),
          day:         getDayName(h.holidayDate),
          type:        h.isOptional ? 'Optional' : 'Mandatory',
          isMandatory: !h.isOptional,
        }));
        this.companyHolidayRows  = rows;
        this.optionalHolidayRows = rows.filter(r => r.type === 'Optional');

        this.pendingWorkedRows = (workedDates ?? []).map((d: any) => ({
          dateWorked: this.fmt(d.date),
          requestedDate: d.requestedDate ? this.fmt(d.requestedDate) : '-',
          status: d.status ?? '-',
          _raw: d,
        }));
      });
  }

  private computeExpiresInDays(expiresOn?: string | null): number | null {
    if (!expiresOn) return null;
    const diff = new Date(expiresOn).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86_400_000));
  }

  // ─── Manager chip ─────────────────────────────────────────────────────────

  onManagerChange(id: string): void {
    this.selectedManager = this.managers.find(m => m.id === id) ?? null;
  }

  removeManager(): void {
    this.selectedManager = null;
    this.availForm.patchValue({ requestToId: '' });
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  onReset(): void {
    this.availForm.reset({ requestToId: '', startDate: null, endDate: null, description: '' });
    this.selectedManager = null;
  }

  onSubmit(): void {
    if (this.availForm.invalid) {
      this.availForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const v = this.availForm.value;
    this.leaveService.availGiftedLeave({
      requestToId:  v.requestToId || undefined,
      startDate:    toISODate(v.startDate),
      endDate:      toISODate(v.endDate),
      description:  v.description || undefined,
    }).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res?.success === false || res?.error) {
          this.showError(res?.error ?? 'Failed to submit request.');
        } else {
          this.showSuccess('Request Submitted!', 'Your gift-of-time avail request has been sent for approval.');
        }
      },
      error: () => { this.isSubmitting = false; },
    });
  }

  private showSuccess(title?: string, desc?: string): void {
    const ref = this.dialog.open(SuccessDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      data: { title, desc },
    });
    ref.afterClosed().subscribe(() => this.navigateBack());
  }

  private showError(message: string): void {
    this.notify.addError(message);
  }

  fmt(d: string | Date): string {
    return formatDateMDY(d);
  }

  navigateBack(): void {
    this.router.navigate(['/ers/leave-management']);
  }
}
